import { Controller } from "@hotwired/stimulus";

const FIELD_TARGETS = {
  full_name: "fullName",
  date_of_birth: "dateOfBirth",
  passport_number: "passportNumber",
  passport_nationality: "passportNationality",
  passport_expiry: "passportExpiry",
};

export default class extends Controller {
  static targets = [
    "input",
    "status",
    "preview",
    "fullName",
    "dateOfBirth",
    "passportNumber",
    "passportNationality",
    "passportExpiry",
    "received",
    "fullNameWarning",
    "dateOfBirthWarning",
    "passportNumberWarning",
    "passportNationalityWarning",
    "passportExpiryWarning",
  ];

  static values = { url: String };

  async scan() {
    const file = this.inputTarget.files[0];

    if (!file) {
      this.setStatus("Choose a passport photo first.");
      return;
    }

    this.clearWarnings();
    this.showPreview(file);
    this.setStatus("Reading passport…");

    const result = await this.upload(file);
    if (result) this.applyResult(result);
  }

  async upload(file) {
    const formData = new FormData();
    formData.append("passport_scan", file);

    let response;

    try {
      response = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": this.csrfToken(),
          Accept: "application/json",
        },
        body: formData,
      });
    } catch (error) {
      console.error("Passport scan: upload failed", error);
      this.setStatus(
        "Could not upload the passport image. Please check your connection and try again.",
      );
      return null;
    } finally {
      this.inputTarget.value = "";
    }

    if (!response.ok) {
      console.error("Passport scan: server returned", response.status);
      this.setStatus("Could not upload the passport image. Please try again.");
      return null;
    }

    try {
      return await response.json();
    } catch (error) {
      console.error("Passport scan: could not parse response", error);
      this.setStatus("Could not read the passport. Please try again.");
      return null;
    }
  }

  applyResult(result) {
    if (result.status !== "success") {
      this.setStatus(
        result.message || "Could not read the passport. Please try again.",
      );
      return;
    }

    Object.entries(FIELD_TARGETS).forEach(([fieldName, targetName]) => {
      const field = result.fields && result.fields[fieldName];
      if (!field || !field.value) return;

      this.fill(targetName, field.value);

      if (field.confidence !== "high") {
        this.showWarning(targetName);
      }
    });

    // Reading a passport means it was handed over, so tick "Passport received"
    // for them. Only ever ticks it — if they deliberately unticked it, a second
    // scan shouldn't quietly override that.
    if (this.hasReceivedTarget && !this.receivedTarget.checked) {
      this.receivedTarget.checked = true;
      this.receivedTarget.dispatchEvent(new Event("change", { bubbles: true }));
      this.flash(this.receivedTarget);
    }

    this.setStatus(
      result.message ||
        "Passport information extracted — please review before saving.",
    );
  }

  showPreview(file) {
    if (!this.hasPreviewTarget) return;

    const previousUrl = this.previewTarget.src;
    this.previewTarget.src = URL.createObjectURL(file);
    this.previewTarget.hidden = false;

    if (previousUrl && previousUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previousUrl);
    }
  }

  fill(targetName, value) {
    if (!this[`has${capitalize(targetName)}Target`]) return;

    const target = this[`${targetName}Target`];
    target.value = value;

    target.dispatchEvent(new Event("input", { bubbles: true }));
    target.dispatchEvent(new Event("change", { bubbles: true }));
    this.flash(target);
  }

  // Briefly highlights a field so the user notices it was just
  // auto-populated from the scan, rather than typed in themselves.
  flash(target) {
    target.classList.remove("passport-scan-fill");
    void target.offsetWidth; // restart the animation if it's already running
    target.classList.add("passport-scan-fill");
    target.addEventListener("animationend", () => target.classList.remove("passport-scan-fill"), { once: true });
  }

  showWarning(targetName) {
    const warningTarget = `${targetName}Warning`;
    if (!this[`has${capitalize(warningTarget)}Target`]) return;

    this[`${warningTarget}Target`].hidden = false;
  }

  clearWarnings() {
    Object.values(FIELD_TARGETS).forEach((targetName) => {
      const warningTarget = `${targetName}Warning`;
      if (this[`has${capitalize(warningTarget)}Target`]) {
        this[`${warningTarget}Target`].hidden = true;
      }
    });
  }

  setStatus(message) {
    this.statusTarget.textContent = message;
    this.statusTarget.hidden = false;
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}

function capitalize(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
